import { z } from 'zod';
import React from 'react';
import { RichTextSchema } from '../richText';
import type { StreamingRichText } from '../richText/streaming';
import type { RichText } from '../richText/interfaces';
import { JSONValueSchema } from '@mutiny-pkg/multiplayer/src/tree/ops/operationSchemas';
import { ThemePropsSchema } from './themeSchema';
import camelCase from 'camelcase';

// Static list of HeroIcon names - avoids importing 324 React components at module load time
// To regenerate: node -e "console.log(JSON.stringify(Object.keys(require('@heroicons/react/24/outline')).sort()))"
const IconNames = [
  'AcademicCapIcon',
  'AdjustmentsHorizontalIcon',
  'AdjustmentsVerticalIcon',
  'ArchiveBoxArrowDownIcon',
  'ArchiveBoxIcon',
  'ArchiveBoxXMarkIcon',
  'ArrowDownCircleIcon',
  'ArrowDownIcon',
  'ArrowDownLeftIcon',
  'ArrowDownOnSquareIcon',
  'ArrowDownOnSquareStackIcon',
  'ArrowDownRightIcon',
  'ArrowDownTrayIcon',
  'ArrowLeftCircleIcon',
  'ArrowLeftEndOnRectangleIcon',
  'ArrowLeftIcon',
  'ArrowLeftOnRectangleIcon',
  'ArrowLeftStartOnRectangleIcon',
  'ArrowLongDownIcon',
  'ArrowLongLeftIcon',
  'ArrowLongRightIcon',
  'ArrowLongUpIcon',
  'ArrowPathIcon',
  'ArrowPathRoundedSquareIcon',
  'ArrowRightCircleIcon',
  'ArrowRightEndOnRectangleIcon',
  'ArrowRightIcon',
  'ArrowRightOnRectangleIcon',
  'ArrowRightStartOnRectangleIcon',
  'ArrowSmallDownIcon',
  'ArrowSmallLeftIcon',
  'ArrowSmallRightIcon',
  'ArrowSmallUpIcon',
  'ArrowTopRightOnSquareIcon',
  'ArrowTrendingDownIcon',
  'ArrowTrendingUpIcon',
  'ArrowTurnDownLeftIcon',
  'ArrowTurnDownRightIcon',
  'ArrowTurnLeftDownIcon',
  'ArrowTurnLeftUpIcon',
  'ArrowTurnRightDownIcon',
  'ArrowTurnRightUpIcon',
  'ArrowTurnUpLeftIcon',
  'ArrowTurnUpRightIcon',
  'ArrowUpCircleIcon',
  'ArrowUpIcon',
  'ArrowUpLeftIcon',
  'ArrowUpOnSquareIcon',
  'ArrowUpOnSquareStackIcon',
  'ArrowUpRightIcon',
  'ArrowUpTrayIcon',
  'ArrowUturnDownIcon',
  'ArrowUturnLeftIcon',
  'ArrowUturnRightIcon',
  'ArrowUturnUpIcon',
  'ArrowsPointingInIcon',
  'ArrowsPointingOutIcon',
  'ArrowsRightLeftIcon',
  'ArrowsUpDownIcon',
  'AtSymbolIcon',
  'BackspaceIcon',
  'BackwardIcon',
  'BanknotesIcon',
  'Bars2Icon',
  'Bars3BottomLeftIcon',
  'Bars3BottomRightIcon',
  'Bars3CenterLeftIcon',
  'Bars3Icon',
  'Bars4Icon',
  'BarsArrowDownIcon',
  'BarsArrowUpIcon',
  'Battery0Icon',
  'Battery100Icon',
  'Battery50Icon',
  'BeakerIcon',
  'BellAlertIcon',
  'BellIcon',
  'BellSlashIcon',
  'BellSnoozeIcon',
  'BoldIcon',
  'BoltIcon',
  'BoltSlashIcon',
  'BookOpenIcon',
  'BookmarkIcon',
  'BookmarkSlashIcon',
  'BookmarkSquareIcon',
  'BriefcaseIcon',
  'BugAntIcon',
  'BuildingLibraryIcon',
  'BuildingOffice2Icon',
  'BuildingOfficeIcon',
  'BuildingStorefrontIcon',
  'CakeIcon',
  'CalculatorIcon',
  'CalendarDateRangeIcon',
  'CalendarDaysIcon',
  'CalendarIcon',
  'CameraIcon',
  'ChartBarIcon',
  'ChartBarSquareIcon',
  'ChartPieIcon',
  'ChatBubbleBottomCenterIcon',
  'ChatBubbleBottomCenterTextIcon',
  'ChatBubbleLeftEllipsisIcon',
  'ChatBubbleLeftIcon',
  'ChatBubbleLeftRightIcon',
  'ChatBubbleOvalLeftEllipsisIcon',
  'ChatBubbleOvalLeftIcon',
  'CheckBadgeIcon',
  'CheckCircleIcon',
  'CheckIcon',
  'ChevronDoubleDownIcon',
  'ChevronDoubleLeftIcon',
  'ChevronDoubleRightIcon',
  'ChevronDoubleUpIcon',
  'ChevronDownIcon',
  'ChevronLeftIcon',
  'ChevronRightIcon',
  'ChevronUpDownIcon',
  'ChevronUpIcon',
  'CircleStackIcon',
  'ClipboardDocumentCheckIcon',
  'ClipboardDocumentIcon',
  'ClipboardDocumentListIcon',
  'ClipboardIcon',
  'ClockIcon',
  'CloudArrowDownIcon',
  'CloudArrowUpIcon',
  'CloudIcon',
  'CodeBracketIcon',
  'CodeBracketSquareIcon',
  'Cog6ToothIcon',
  'Cog8ToothIcon',
  'CogIcon',
  'CommandLineIcon',
  'ComputerDesktopIcon',
  'CpuChipIcon',
  'CreditCardIcon',
  'CubeIcon',
  'CubeTransparentIcon',
  'CurrencyBangladeshiIcon',
  'CurrencyDollarIcon',
  'CurrencyEuroIcon',
  'CurrencyPoundIcon',
  'CurrencyRupeeIcon',
  'CurrencyYenIcon',
  'CursorArrowRaysIcon',
  'CursorArrowRippleIcon',
  'DevicePhoneMobileIcon',
  'DeviceTabletIcon',
  'DivideIcon',
  'DocumentArrowDownIcon',
  'DocumentArrowUpIcon',
  'DocumentChartBarIcon',
  'DocumentCheckIcon',
  'DocumentCurrencyBangladeshiIcon',
  'DocumentCurrencyDollarIcon',
  'DocumentCurrencyEuroIcon',
  'DocumentCurrencyPoundIcon',
  'DocumentCurrencyRupeeIcon',
  'DocumentCurrencyYenIcon',
  'DocumentDuplicateIcon',
  'DocumentIcon',
  'DocumentMagnifyingGlassIcon',
  'DocumentMinusIcon',
  'DocumentPlusIcon',
  'DocumentTextIcon',
  'EllipsisHorizontalCircleIcon',
  'EllipsisHorizontalIcon',
  'EllipsisVerticalIcon',
  'EnvelopeIcon',
  'EnvelopeOpenIcon',
  'EqualsIcon',
  'ExclamationCircleIcon',
  'ExclamationTriangleIcon',
  'EyeDropperIcon',
  'EyeIcon',
  'EyeSlashIcon',
  'FaceFrownIcon',
  'FaceSmileIcon',
  'FilmIcon',
  'FingerPrintIcon',
  'FireIcon',
  'FlagIcon',
  'FolderArrowDownIcon',
  'FolderIcon',
  'FolderMinusIcon',
  'FolderOpenIcon',
  'FolderPlusIcon',
  'ForwardIcon',
  'FunnelIcon',
  'GifIcon',
  'GiftIcon',
  'GiftTopIcon',
  'GlobeAltIcon',
  'GlobeAmericasIcon',
  'GlobeAsiaAustraliaIcon',
  'GlobeEuropeAfricaIcon',
  'H1Icon',
  'H2Icon',
  'H3Icon',
  'HandRaisedIcon',
  'HandThumbDownIcon',
  'HandThumbUpIcon',
  'HashtagIcon',
  'HeartIcon',
  'HomeIcon',
  'HomeModernIcon',
  'IdentificationIcon',
  'InboxArrowDownIcon',
  'InboxIcon',
  'InboxStackIcon',
  'InformationCircleIcon',
  'ItalicIcon',
  'KeyIcon',
  'LanguageIcon',
  'LifebuoyIcon',
  'LightBulbIcon',
  'LinkIcon',
  'LinkSlashIcon',
  'ListBulletIcon',
  'LockClosedIcon',
  'LockOpenIcon',
  'MagnifyingGlassCircleIcon',
  'MagnifyingGlassIcon',
  'MagnifyingGlassMinusIcon',
  'MagnifyingGlassPlusIcon',
  'MapIcon',
  'MapPinIcon',
  'MegaphoneIcon',
  'MicrophoneIcon',
  'MinusCircleIcon',
  'MinusIcon',
  'MinusSmallIcon',
  'MoonIcon',
  'MusicalNoteIcon',
  'NewspaperIcon',
  'NoSymbolIcon',
  'NumberedListIcon',
  'PaintBrushIcon',
  'PaperAirplaneIcon',
  'PaperClipIcon',
  'PauseCircleIcon',
  'PauseIcon',
  'PencilIcon',
  'PencilSquareIcon',
  'PercentBadgeIcon',
  'PhoneArrowDownLeftIcon',
  'PhoneArrowUpRightIcon',
  'PhoneIcon',
  'PhoneXMarkIcon',
  'PhotoIcon',
  'PlayCircleIcon',
  'PlayIcon',
  'PlayPauseIcon',
  'PlusCircleIcon',
  'PlusIcon',
  'PlusSmallIcon',
  'PowerIcon',
  'PresentationChartBarIcon',
  'PresentationChartLineIcon',
  'PrinterIcon',
  'PuzzlePieceIcon',
  'QrCodeIcon',
  'QuestionMarkCircleIcon',
  'QueueListIcon',
  'RadioIcon',
  'ReceiptPercentIcon',
  'ReceiptRefundIcon',
  'RectangleGroupIcon',
  'RectangleStackIcon',
  'RocketLaunchIcon',
  'RssIcon',
  'ScaleIcon',
  'ScissorsIcon',
  'ServerIcon',
  'ServerStackIcon',
  'ShareIcon',
  'ShieldCheckIcon',
  'ShieldExclamationIcon',
  'ShoppingBagIcon',
  'ShoppingCartIcon',
  'SignalIcon',
  'SignalSlashIcon',
  'SlashIcon',
  'SparklesIcon',
  'SpeakerWaveIcon',
  'SpeakerXMarkIcon',
  'Square2StackIcon',
  'Square3Stack3DIcon',
  'Squares2X2Icon',
  'SquaresPlusIcon',
  'StarIcon',
  'StopCircleIcon',
  'StopIcon',
  'StrikethroughIcon',
  'SunIcon',
  'SwatchIcon',
  'TableCellsIcon',
  'TagIcon',
  'TicketIcon',
  'TrashIcon',
  'TrophyIcon',
  'TruckIcon',
  'TvIcon',
  'UnderlineIcon',
  'UserCircleIcon',
  'UserGroupIcon',
  'UserIcon',
  'UserMinusIcon',
  'UserPlusIcon',
  'UsersIcon',
  'VariableIcon',
  'VideoCameraIcon',
  'VideoCameraSlashIcon',
  'ViewColumnsIcon',
  'ViewfinderCircleIcon',
  'WalletIcon',
  'WifiIcon',
  'WindowIcon',
  'WrenchIcon',
  'WrenchScrewdriverIcon',
  'XCircleIcon',
  'XMarkIcon',
] as const;

export type IconName = (typeof IconNames)[number];
export { IconNames };
export const formatToIconName = (hyphenatedString: string) => {
  try {
    const baseName = camelCase(hyphenatedString, { pascalCase: true });
    const iconName = baseName.endsWith('Icon') ? baseName : `${baseName}Icon`;
    return iconName;
  } catch (error) {
    console.warn(`Error formatting icon name: ${error}`);
    return '';
  }
};
// If we need to provide all the possible values to the llm, we can do this
// export const formatToHyphenatedIconName = (pascalString: string) => decamelize(pascalString, { separator: '-' });
// export const HyphenatedIconNames = IconNames.map(formatToHyphenatedIconName);

export enum FlexiblePageTreeNodeType {
  Page = 'page',
  Section = 'section',
  Container = 'container',
  Text = 'text',
  Button = 'button',
  StructuredButton = 'structured-button',
  Image = 'image',
  Icon = 'icon',
  Svg = 'svg',
  Video = 'video',
  Embed = 'embed',
  Dialog = 'dialog',
  TabGroup = 'tab-group',
  TabList = 'tab-list',
  TabPanels = 'tab-panels',
  Disclosure = 'disclosure',
  DisclosureContent = 'disclosure-content',
}

export enum EmbedType {
  URL = 'url',
  Code = 'code',
}

const RichTextOrStringSchema = RichTextSchema.latestVersionSchema.or(z.string());

// Regex to detect disallowed Tailwind breakpoint prefixes (blacklist approach)
// Allows: lg: breakpoint prefix (for desktop-specific overrides) and all non-breakpoint prefixes like hover:, focus:, dark:, etc.
// Blocks: xs:, sm:, md:, xl:, 2xl:, 3xl:, 4xl:, 5xl:, etc.
const DISALLOWED_PREFIX_REGEX = /\b(xs:|sm:|md:|xl:|\d+xl:)/;

// Validated className schema for generation - allows lg: breakpoint prefix for desktop overrides
const ValidatedClassNameSchema = z
  .string()
  .optional()
  .describe(
    'Tailwind CSS classes for styling. Use lg: prefix for desktop-specific overrides (base styles apply to mobile). Example: "text-base lg:text-xl" means text-base on mobile, text-xl on desktop. NEVER use sm:, md:, xl:, 2xl: prefixes.',
  )
  .refine(
    (value) => {
      if (!value) return true; // undefined or empty is valid
      // Reject if any disallowed breakpoint prefixes are found (lg: is allowed)
      return !DISALLOWED_PREFIX_REGEX.test(value);
    },
    {
      message:
        'className contains disallowed Tailwind breakpoint prefixes. Only lg: is allowed for desktop overrides. Never use sm:, md:, xl:, 2xl:, etc.',
    },
  );

// Base schema for all tree node data - includes className, type, and flexible JSONValue properties
const BaseTreeNodeDataSchema = z
  .object({
    className: z.string().optional(),
    type: z.nativeEnum(FlexiblePageTreeNodeType),
  })
  .catchall(JSONValueSchema);

// Base schema for all tree nodes - includes id, data, and optional children
// Note: type field is in data, not at top level
const BaseTreeNodeSchema = z.object({
  id: z.string(),
  data: BaseTreeNodeDataSchema,
  children: z.array(z.any()).optional(), // Will be refined in specific schemas
});

// Inline node types for FlexibleTextContent — all use { data: { type, ...props }, children? } shape
const FlexibleInlineTextNodeSchema = z.object({
  data: z.object({
    type: z.literal('text'),
    text: z.string(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    strikethrough: z.boolean().optional(),
    underline: z.boolean().optional(),
    color: z.string().optional(),
    highlight: z.string().optional(),
  }),
});

const FlexibleInlineVariableNodeSchema = z.object({
  data: z.object({
    type: z.literal('variable'),
    columnId: z.string(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    strikethrough: z.boolean().optional(),
    underline: z.boolean().optional(),
    color: z.string().optional(),
    highlight: z.string().optional(),
  }),
});

const FlexibleInlineLinebreakNodeSchema = z.object({
  data: z.object({ type: z.literal('linebreak') }),
});

// Non-link inline nodes — used as link children to prevent nested anchor elements
const FlexibleSimpleInlineNodeSchema = z.union([
  FlexibleInlineTextNodeSchema,
  FlexibleInlineVariableNodeSchema,
  FlexibleInlineLinebreakNodeSchema,
]);

const FlexibleInlineLinkNodeSchema = z.object({
  data: z.object({ type: z.literal('link'), href: z.string() }),
  children: z.array(FlexibleSimpleInlineNodeSchema),
});

export const FlexibleInlineNodeSchema = z.union([FlexibleSimpleInlineNodeSchema, FlexibleInlineLinkNodeSchema]);

const FlexibleParagraphNodeSchema = z.object({
  data: z.object({
    type: z.literal('paragraph'),
    format: z.enum(['left', 'center', 'right', 'justify', '']).optional(),
  }),
  children: z.array(FlexibleInlineNodeSchema),
});

const FlexibleListItemNodeSchema = z.object({
  data: z.object({ type: z.literal('listitem') }),
  children: z.array(FlexibleInlineNodeSchema),
});

const FlexibleListNodeSchema = z.object({
  data: z.object({ type: z.literal('list'), listType: z.enum(['ordered', 'unordered']) }),
  children: z.array(FlexibleListItemNodeSchema),
});

export const FlexibleBlockNodeSchema = z.union([FlexibleParagraphNodeSchema, FlexibleListNodeSchema]);

export const FlexibleTextContentSchema = z.array(FlexibleBlockNodeSchema);

export type FlexibleInlineTextNode = z.infer<typeof FlexibleInlineTextNodeSchema>;
export type FlexibleInlineVariableNode = z.infer<typeof FlexibleInlineVariableNodeSchema>;
export type FlexibleInlineLinebreakNode = z.infer<typeof FlexibleInlineLinebreakNodeSchema>;
export type FlexibleSimpleInlineNode = z.infer<typeof FlexibleSimpleInlineNodeSchema>;
export type FlexibleInlineLinkNode = z.infer<typeof FlexibleInlineLinkNodeSchema>;
export type FlexibleInlineNode = z.infer<typeof FlexibleInlineNodeSchema>;
export type FlexibleParagraphNode = z.infer<typeof FlexibleParagraphNodeSchema>;
export type FlexibleListItemNode = z.infer<typeof FlexibleListItemNodeSchema>;
export type FlexibleListNode = z.infer<typeof FlexibleListNodeSchema>;
export type FlexibleBlockNode = z.infer<typeof FlexibleBlockNodeSchema>;
export type FlexibleTextContent = z.infer<typeof FlexibleTextContentSchema>;

const FlexibleOrRichTextOrStringSchema = FlexibleTextContentSchema.or(RichTextOrStringSchema);

// Common schema definitions (without catchall or strict) - shared between standard and generation schemas
const ButtonTreeNodeDataBase = z.object({
  href: RichTextOrStringSchema.optional(),
  text: FlexibleOrRichTextOrStringSchema.optional(),
  disabled: z.boolean().optional(),
  openInNewTab: z.boolean().optional(),
  variant: z.enum(['primary', 'secondary', 'tertiary', 'outline', 'ghost', 'link', 'custom']),
  className: z.string().optional(),
  commandfor: z.string().optional(),
  command: z.enum(['close', 'show', 'show-modal', '--toggle', '--show', '--hide']).optional(),
  role: z.string().optional(),
  ariaLabel: z.string().optional(),
});

export const ButtonTreeNodeDataSchema = ButtonTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Button),
}).catchall(JSONValueSchema);
export const ButtonTreeNodeDataGenerationSchema = ButtonTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Button),
  className: ValidatedClassNameSchema,
});

const StructuredButtonTreeNodeDataBase = z.object({
  className: z.string().optional(),
  disabled: z.boolean().optional(),
  ariaLabel: z.string().optional(),
  command: z.enum(['close', 'show', 'show-modal', '--toggle', '--show', '--hide']).optional(),
  commandfor: z.string().optional(),
  role: z.string().optional(),
  href: RichTextOrStringSchema.optional(),
  openInNewTab: z.boolean().optional(),
});

const StructuredButtonTreeNodeDataSchema = StructuredButtonTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.StructuredButton),
}).catchall(JSONValueSchema);
const StructuredButtonTreeNodeDataGenerationSchema = StructuredButtonTreeNodeDataBase.omit({
  className: true,
}).extend({
  type: z.literal(FlexiblePageTreeNodeType.StructuredButton),
  className: ValidatedClassNameSchema,
});

export const VOID_ELEMENTS = [
  'a',
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'source',
  'track',
  'wbr',
];

const TextTreeNodeDataBase = z.object({
  as: z
    .custom<keyof React.JSX.IntrinsicElements>()
    .optional()
    .refine((value) => !value || !VOID_ELEMENTS.includes(value), {
      message: 'Void elements are not allowed as container elements.',
    })
    .describe(
      `The HTML element to render as. Defaults to "span". Void elements (${VOID_ELEMENTS.join(', ')}) are not allowed.`,
    ),
  text: FlexibleOrRichTextOrStringSchema,
  variant: z.enum(['default', 'body', 'heading1', 'heading2', 'heading3', 'heading4', 'overline']).optional(),
  className: z.string().optional(),
});

export const TextTreeNodeDataSchema = TextTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Text),
}).catchall(JSONValueSchema);
export const TextTreeNodeDataGenerationSchema = TextTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Text),
  className: ValidatedClassNameSchema,
});

const ContainerTreeNodeDataBase = z.object({
  as: z
    .custom<keyof React.JSX.IntrinsicElements>()
    .optional()
    .refine((value) => !value || !VOID_ELEMENTS.includes(value), {
      message: 'Void elements are not allowed as container elements.',
    })
    .describe(
      `The HTML element to render as. Defaults to "div". Void elements (${VOID_ELEMENTS.join(', ')}) are not allowed.`,
    ),
  backgroundSrc: z.string().optional(),
  className: z.string().optional(),
  decorative: z
    .boolean()
    .optional()
    .describe(
      'Set to true for containers used purely for visual effects (backgrounds, borders, ambient light) with no meaningful content children. Set to false (or omit) for structural containers that hold actual content.',
    ),
  href: RichTextOrStringSchema.optional().describe(
    'Optional URL to make the entire container clickable. When present, the container renders as an anchor element.',
  ),
  openInNewTab: z
    .boolean()
    .optional()
    .describe('Whether the link should open in a new tab. Only applies when href is set.'),
});

const ContainerTreeNodeDataSchema = ContainerTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Container),
}).catchall(JSONValueSchema);
export const ContainerTreeNodeDataGenerationSchema = ContainerTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Container),
  className: ValidatedClassNameSchema,
});

export const PageTreeNodeDataSchema = BaseTreeNodeDataSchema.extend({
  pageTitle: z.string().optional(),
  favicon: z.string().optional(),
  ogImage: z.string().optional(),
  designGuidelines: z.string().optional(),
  pageDescription: z.string().optional(),
  pageHeadHtml: z.string().optional(),
  pageBodyHtml: z.string().optional(),
  allowSearchIndexing: z.boolean().optional(),
  allowLinkFollowing: z.boolean().optional(),
  as: z.custom<keyof React.JSX.IntrinsicElements>().optional(),
}).merge(ThemePropsSchema);

const ImageTreeNodeDataBase = z.object({
  src: RichTextOrStringSchema,
  alt: z.string().optional(),
  className: z.string().optional(),
  imageHref: RichTextOrStringSchema.optional(),
  openInNewTab: z.boolean().optional(),
});

const ImageTreeNodeDataSchema = ImageTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Image),
}).catchall(JSONValueSchema);
export const ImageTreeNodeDataGenerationSchema = ImageTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Image),
  className: ValidatedClassNameSchema,
});

const VideoTreeNodeDataBase = z.object({
  src: z.string(),
  alt: z.string().optional(),
  poster: z.string().optional(),
  controls: z.boolean().optional(),
  autoplay: z.boolean().optional(),
  muted: z.boolean().optional(),
  loop: z.boolean().optional(),
  trackSrc: z.string().optional(),
  trackLabel: z.string().optional(),
  trackSrcLang: z.string().optional(),
  className: z.string().optional(),
});

const VideoTreeNodeDataSchema = VideoTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Video),
}).catchall(JSONValueSchema);
export const VideoTreeNodeDataGenerationSchema = VideoTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Video),
  className: ValidatedClassNameSchema,
});

const IconTreeNodeDataBase = z.object({
  name: z.string().superRefine((value, ctx) => {
    const formattedName = formatToIconName(value);
    if (!IconNames.includes(formattedName as IconName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Invalid icon name: "${value}" (formatted to: ${formattedName}). Use hyphenated names like "arrow-right", "check-circle", "user".`,
      });
    }
  }),
  className: z.string().optional(),
  href: RichTextOrStringSchema.optional(),
  openInNewTab: z.boolean().optional(),
});

const IconTreeNodeDataSchema = IconTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Icon),
}).catchall(JSONValueSchema);
export const IconTreeNodeDataGenerationSchema = IconTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Icon),
  className: ValidatedClassNameSchema,
});

const SvgTreeNodeDataBase = z.object({
  svg: z.string(),
  viewBox: z.string().optional(),
  className: z.string().optional(),
});

const SvgTreeNodeDataSchema = SvgTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Svg),
}).catchall(JSONValueSchema);
export const SvgTreeNodeDataGenerationSchema = SvgTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Svg),
  className: ValidatedClassNameSchema,
});

const EmbedTreeNodeDataBase = z.object({
  embedType: z.nativeEnum(EmbedType),
  url: z.string().optional(),
  codeSnippet: z.string().optional(),
  className: z.string().optional(),
});

const EmbedTreeNodeDataSchema = EmbedTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Embed),
}).catchall(JSONValueSchema);
export const EmbedTreeNodeDataGenerationSchema = EmbedTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Embed),
  className: ValidatedClassNameSchema,
});

const DialogTreeNodeDataBase = z.object({
  ariaLabel: z.string().optional(),
  className: z.string().optional(),
  domTargetId: z.string(),
});

const DialogTreeNodeDataSchema = DialogTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Dialog),
}).catchall(JSONValueSchema);
const DialogTreeNodeDataGenerationSchema = DialogTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Dialog),
  className: ValidatedClassNameSchema,
});

const TabGroupTreeNodeDataBase = z.object({
  className: z.string().optional(),
});

const TabGroupTreeNodeDataSchema = TabGroupTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.TabGroup),
}).catchall(JSONValueSchema);
const TabGroupTreeNodeDataGenerationSchema = TabGroupTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.TabGroup),
  className: ValidatedClassNameSchema,
});

const TabListTreeNodeDataBase = z.object({
  className: z.string().optional(),
});

const TabListTreeNodeDataSchema = TabListTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.TabList),
}).catchall(JSONValueSchema);
const TabListTreeNodeDataGenerationSchema = TabListTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.TabList),
  className: ValidatedClassNameSchema,
});

const TabPanelsTreeNodeDataBase = z.object({
  className: z.string().optional(),
});

const TabPanelsTreeNodeDataSchema = TabPanelsTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.TabPanels),
}).catchall(JSONValueSchema);
const TabPanelsTreeNodeDataGenerationSchema = TabPanelsTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.TabPanels),
  className: ValidatedClassNameSchema,
});

const DisclosureTreeNodeDataBase = z.object({
  className: z.string().optional(),
});

const DisclosureTreeNodeDataSchema = DisclosureTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Disclosure),
}).catchall(JSONValueSchema);
const DisclosureTreeNodeDataGenerationSchema = DisclosureTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Disclosure),
  className: ValidatedClassNameSchema,
});

const DisclosureContentTreeNodeDataBase = z.object({
  domTargetId: z.string(),
  defaultOpen: z.boolean().optional(),
  className: z.string().optional(),
});

const DisclosureContentTreeNodeDataSchema = DisclosureContentTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.DisclosureContent),
}).catchall(JSONValueSchema);
const DisclosureContentTreeNodeDataGenerationSchema = DisclosureContentTreeNodeDataBase.omit({
  className: true,
}).extend({
  type: z.literal(FlexiblePageTreeNodeType.DisclosureContent),
  className: ValidatedClassNameSchema,
});

const SectionTreeNodeDataBase = z.object({
  backgroundSrc: z.string().optional(),
  className: z.string().optional(),
});

const SectionTreeNodeDataSchema = SectionTreeNodeDataBase.extend({
  type: z.literal(FlexiblePageTreeNodeType.Section),
}).catchall(JSONValueSchema);
export const SectionTreeNodeDataGenerationSchema = SectionTreeNodeDataBase.omit({ className: true }).extend({
  type: z.literal(FlexiblePageTreeNodeType.Section),
  className: ValidatedClassNameSchema,
});

const ButtonTreeNodeSchema: z.ZodType<ButtonTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: ButtonTreeNodeDataSchema,
    children: z.array(InteractiveChildNodeSchema).optional(),
  }),
);

// Interactive child nodes - allows leaf nodes and interactive containers (for StructuredButton context)
const InteractiveChildNodeSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    TextTreeNodeSchema,
    ImageTreeNodeSchema,
    VideoTreeNodeSchema,
    IconTreeNodeSchema,
    SvgTreeNodeSchema,
    InteractiveContainerTreeNodeSchema,
  ]),
);

// Interactive Container - recursively allows only interactive children
const InteractiveContainerTreeNodeSchema: z.ZodType<any> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: ContainerTreeNodeDataSchema,
    children: z.array(InteractiveChildNodeSchema).optional(),
  }),
);

const StructuredButtonTreeNodeSchema: z.ZodType<StructuredButtonTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: StructuredButtonTreeNodeDataSchema,
    children: z.array(InteractiveChildNodeSchema).optional(),
  }),
);

export const TextTreeNodeSchema = BaseTreeNodeSchema.extend({
  data: TextTreeNodeDataSchema,
  children: z.undefined().optional(),
});

export const ImageTreeNodeSchema = BaseTreeNodeSchema.extend({
  data: ImageTreeNodeDataSchema,
  children: z.undefined().optional(),
});

export const VideoTreeNodeSchema = BaseTreeNodeSchema.extend({
  data: VideoTreeNodeDataSchema,
  children: z.undefined().optional(),
});

export const IconTreeNodeSchema = BaseTreeNodeSchema.extend({
  data: IconTreeNodeDataSchema,
  children: z.undefined().optional(),
});

export const SvgTreeNodeSchema = BaseTreeNodeSchema.extend({
  data: SvgTreeNodeDataSchema,
  children: z.undefined().optional(),
});

export const EmbedTreeNodeSchema = BaseTreeNodeSchema.extend({
  data: EmbedTreeNodeDataSchema,
  children: z.undefined().optional(),
});

const DialogTreeNodeSchema: z.ZodType<DialogTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: DialogTreeNodeDataSchema,
    children: z.array(ComponentTreeNodeSchema),
  }),
);

const TabGroupTreeNodeSchema: z.ZodType<TabGroupTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: TabGroupTreeNodeDataSchema,
    children: z.union([
      z.tuple([TabListTreeNodeSchema, TabPanelsTreeNodeSchema]),
      z.tuple([TabPanelsTreeNodeSchema, TabListTreeNodeSchema]),
    ]),
  }),
);

const TabListTreeNodeSchema: z.ZodType<TabListTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: TabListTreeNodeDataSchema,
    children: z.array(z.union([ButtonTreeNodeSchema, StructuredButtonTreeNodeSchema])),
  }),
);

const TabPanelsTreeNodeSchema: z.ZodType<TabPanelsTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: TabPanelsTreeNodeDataSchema,
    children: z.array(z.lazy(() => ContainerTreeNodeSchema)),
  }),
);

const DisclosureContentTreeNodeSchema: z.ZodType<DisclosureContentTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: DisclosureContentTreeNodeDataSchema,
    children: z.array(z.lazy(() => ComponentTreeNodeSchema)).optional(),
  }),
);

const DisclosureTreeNodeSchema: z.ZodType<DisclosureTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: DisclosureTreeNodeDataSchema,
    children: z.tuple([
      z.union([
        ButtonTreeNodeSchema.refine(
          (button) => button.data.command !== undefined && button.data.commandfor !== undefined,
          { message: 'Disclosure trigger must have command and commandfor' },
        ),
        StructuredButtonTreeNodeSchema.refine(
          (button) => button.data.command !== undefined && button.data.commandfor !== undefined,
          { message: 'Disclosure trigger must have command and commandfor' },
        ),
      ]),
      DisclosureContentTreeNodeSchema,
    ]),
  }),
);

const SectionTreeNodeSchema: z.ZodType<SectionTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: SectionTreeNodeDataSchema,
    children: z.array(z.lazy(() => ComponentTreeNodeSchema)).optional(),
  }),
);

const ContainerTreeNodeSchema: z.ZodType<ContainerTreeNode> = z.lazy(() =>
  BaseTreeNodeSchema.extend({
    data: ContainerTreeNodeDataSchema,
    children: z.array(z.lazy(() => ComponentTreeNodeSchema)).optional(),
  }),
);

// Derive TypeScript types from schemas - these are strict, non-streaming types
export type ButtonTreeNodeData = z.infer<typeof ButtonTreeNodeDataSchema>;
export type TextTreeNodeData = z.infer<typeof TextTreeNodeDataSchema>;
export type ContainerTreeNodeData = z.infer<typeof ContainerTreeNodeDataSchema>;
export type PageTreeNodeData = z.infer<typeof PageTreeNodeDataSchema>;
export type DialogTreeNodeData = z.infer<typeof DialogTreeNodeDataSchema>;
export type TabGroupTreeNodeData = z.infer<typeof TabGroupTreeNodeDataSchema>;
export type TabListTreeNodeData = z.infer<typeof TabListTreeNodeDataSchema>;
export type TabPanelsTreeNodeData = z.infer<typeof TabPanelsTreeNodeDataSchema>;
export type DisclosureTreeNodeData = z.infer<typeof DisclosureTreeNodeDataSchema>;
export type DisclosureContentTreeNodeData = z.infer<typeof DisclosureContentTreeNodeDataSchema>;
export type SectionTreeNodeData = z.infer<typeof SectionTreeNodeDataSchema>;

export type ButtonTreeNode = {
  id: string;
  data: ButtonTreeNodeData;
  children?: InteractiveChildNode[];
};

export type StructuredButtonTreeNodeData = z.infer<typeof StructuredButtonTreeNodeDataSchema>;

// Interactive Container type - recursively restricts children to interactive types
export type InteractiveContainerTreeNode = {
  id: string;
  data: ContainerTreeNodeData;
  children?: InteractiveChildNode[];
};

// Union type for all interactive children (leaf nodes use existing types)
export type InteractiveChildNode =
  | TextTreeNode
  | ImageTreeNode
  | VideoTreeNode
  | IconTreeNode
  | SvgTreeNode
  | InteractiveContainerTreeNode;

export type StructuredButtonTreeNode = {
  id: string;
  data: StructuredButtonTreeNodeData;
  children?: InteractiveChildNode[];
};

export type TextTreeNode = z.infer<typeof TextTreeNodeSchema>;
export type PageTreeNode = z.infer<typeof PageTreeNodeSchema>;
export type ImageTreeNode = z.infer<typeof ImageTreeNodeSchema>;
export type VideoTreeNode = z.infer<typeof VideoTreeNodeSchema>;
export type IconTreeNode = z.infer<typeof IconTreeNodeSchema>;
export type SvgTreeNode = z.infer<typeof SvgTreeNodeSchema>;
export type EmbedTreeNode = z.infer<typeof EmbedTreeNodeSchema>;

// Define section/container tree node types manually to avoid circular dependency
export type SectionTreeNode = {
  id: string;
  data: SectionTreeNodeData;
  children?: ComponentTreeNode[];
};

export type ContainerTreeNode = {
  id: string;
  data: ContainerTreeNodeData;
  children?: ComponentTreeNode[];
};

export type DialogTreeNode = {
  id: string;
  data: DialogTreeNodeData;
  children?: ComponentTreeNode[];
};

export type TabGroupTreeNode = {
  id: string;
  data: TabGroupTreeNodeData;
  children?: [TabListTreeNode, TabPanelsTreeNode] | [TabPanelsTreeNode, TabListTreeNode];
};

export type TabListTreeNode = {
  id: string;
  data: TabListTreeNodeData;
  children?: (ButtonTreeNode | StructuredButtonTreeNode)[];
};

export type TabPanelsTreeNode = {
  id: string;
  data: TabPanelsTreeNodeData;
  children?: ContainerTreeNode[];
};

export type DisclosureContentTreeNode = {
  id: string;
  data: DisclosureContentTreeNodeData;
  children?: ComponentTreeNode[];
};

export type DisclosureTreeNode = {
  id: string;
  data: DisclosureTreeNodeData;
  children?: [ButtonTreeNode | StructuredButtonTreeNode, DisclosureContentTreeNode];
};

// Define ComponentTreeNode type to avoid implicit any type
export type ComponentTreeNode =
  | ButtonTreeNode
  | StructuredButtonTreeNode
  | TextTreeNode
  | ImageTreeNode
  | VideoTreeNode
  | IconTreeNode
  | SvgTreeNode
  | EmbedTreeNode
  | DialogTreeNode
  | ContainerTreeNode
  | SectionTreeNode
  | TabGroupTreeNode
  | TabListTreeNode
  | TabPanelsTreeNode
  | DisclosureTreeNode
  | DisclosureContentTreeNode;

export const ComponentTreeNodeSchema: z.ZodType<ComponentTreeNode> = z.lazy(() =>
  z.union([
    ButtonTreeNodeSchema,
    StructuredButtonTreeNodeSchema,
    TextTreeNodeSchema,
    ImageTreeNodeSchema,
    VideoTreeNodeSchema,
    IconTreeNodeSchema,
    SvgTreeNodeSchema,
    EmbedTreeNodeSchema,
    DialogTreeNodeSchema,
    ContainerTreeNodeSchema,
    SectionTreeNodeSchema,
    TabGroupTreeNodeSchema,
    TabListTreeNodeSchema,
    TabPanelsTreeNodeSchema,
    DisclosureTreeNodeSchema,
    DisclosureContentTreeNodeSchema,
  ]),
);

// Note: preprocessNodeType was removed as it's no longer needed.
// The schema now expects type only in data, not at the top level.

// Interactive child generation schema for StructuredButton context
const InteractiveChildNodeGenerationSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    z.object({
      data: TextTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: ImageTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: VideoTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: IconTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: SvgTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: ContainerTreeNodeDataGenerationSchema,
      children: z.array(z.lazy(() => InteractiveChildNodeGenerationSchema)).optional(),
    }),
  ]),
);

const TabListTreeNodeGenerationSchema = z.object({
  data: TabListTreeNodeDataGenerationSchema,
  children: z.array(
    z.union([
      z.object({
        data: ButtonTreeNodeDataGenerationSchema.extend({
          role: z.literal('tab'),
        }),
        children: z.array(InteractiveChildNodeGenerationSchema).optional(),
      }),
      z.object({
        data: StructuredButtonTreeNodeDataGenerationSchema.extend({
          role: z.literal('tab'),
        }),
        children: z.array(InteractiveChildNodeGenerationSchema).optional(),
      }),
    ]),
  ),
});

const TabPanelsTreeNodeGenerationSchema = z.object({
  data: TabPanelsTreeNodeDataGenerationSchema,
  children: z.array(
    z.object({
      data: ContainerTreeNodeDataGenerationSchema,
      children: z.array(z.lazy(() => ComponentTreeNodeGenerationSchema)).optional(),
    }),
  ),
});

export const ComponentTreeNodeGenerationSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    z.object({
      data: ButtonTreeNodeDataGenerationSchema,
      children: z.array(InteractiveChildNodeGenerationSchema).optional(),
    }),
    z.object({
      data: StructuredButtonTreeNodeDataGenerationSchema,
      children: z.array(InteractiveChildNodeGenerationSchema).optional(),
    }),
    z.object({
      data: TextTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: ImageTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: VideoTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: IconTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: SvgTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: EmbedTreeNodeDataGenerationSchema,
    }),
    z.object({
      data: DialogTreeNodeDataGenerationSchema,
      children: z.array(z.lazy(() => ComponentTreeNodeGenerationSchema)).optional(),
    }),
    z.object({
      data: ContainerTreeNodeDataGenerationSchema,
      children: z.array(z.lazy(() => ComponentTreeNodeGenerationSchema)).optional(),
    }),
    z.object({
      data: SectionTreeNodeDataGenerationSchema,
      children: z.array(z.lazy(() => ComponentTreeNodeGenerationSchema)).optional(),
    }),
    z.object({
      data: TabGroupTreeNodeDataGenerationSchema,
      children: z.union([
        z.tuple([TabListTreeNodeGenerationSchema, TabPanelsTreeNodeGenerationSchema]),
        z.tuple([TabPanelsTreeNodeGenerationSchema, TabListTreeNodeGenerationSchema]),
      ]),
    }),
    z.object({
      data: DisclosureTreeNodeDataGenerationSchema,
      children: z.tuple([
        z.union([
          z
            .object({
              data: ButtonTreeNodeDataGenerationSchema,
              children: z.array(InteractiveChildNodeGenerationSchema).optional(),
            })
            .refine((button) => button.data.command !== undefined && button.data.commandfor !== undefined, {
              message: 'Disclosure triggers must have command and commandfor',
            }),
          z
            .object({
              data: StructuredButtonTreeNodeDataGenerationSchema,
              children: z.array(InteractiveChildNodeGenerationSchema).optional(),
            })
            .refine((button) => button.data.command !== undefined && button.data.commandfor !== undefined, {
              message: 'Disclosure triggers must have command and commandfor',
            }),
        ]),
        z.object({
          data: DisclosureContentTreeNodeDataGenerationSchema,
          children: z.array(z.lazy(() => ComponentTreeNodeGenerationSchema)).optional(),
        }),
      ]),
    }),
  ]),
);

export const PageTreeNodeSchema = BaseTreeNodeSchema.extend({
  data: PageTreeNodeDataSchema,
  children: z.array(SectionTreeNodeSchema).optional(),
});

export type FlexiblePageTreeNode = PageTreeNode;

// Base flexible page tree node type - includes type information and is available for any flexible page node
export type BaseFlexiblePageTreeNode = z.infer<typeof BaseTreeNodeSchema>;

// Type guard functions for flexible page tree nodes
export const isTextTreeNode = (node: BaseFlexiblePageTreeNode): node is TextTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.Text;
};

export const isButtonTreeNode = (node: BaseFlexiblePageTreeNode): node is ButtonTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.Button;
};

export const isStructuredButtonTreeNode = (node: BaseFlexiblePageTreeNode): node is StructuredButtonTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.StructuredButton;
};

export const isImageTreeNode = (node: BaseFlexiblePageTreeNode): node is ImageTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.Image;
};

export const isContainerTreeNode = (node: BaseFlexiblePageTreeNode): node is ContainerTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.Container;
};

export const isSectionTreeNode = (node: BaseFlexiblePageTreeNode): node is SectionTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.Section;
};

export const isIconTreeNode = (node: BaseFlexiblePageTreeNode): node is IconTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.Icon;
};

export const isSvgTreeNode = (node: BaseFlexiblePageTreeNode): node is SvgTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.Svg;
};

export const isEmbedTreeNode = (node: BaseFlexiblePageTreeNode): node is EmbedTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.Embed;
};

export const isTabGroupTreeNode = (node: BaseFlexiblePageTreeNode): node is TabGroupTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.TabGroup;
};

export const isTabListTreeNode = (node: BaseFlexiblePageTreeNode): node is TabListTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.TabList;
};

export const isTabPanelsTreeNode = (node: BaseFlexiblePageTreeNode): node is TabPanelsTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.TabPanels;
};

export const isDisclosureContentTreeNode = (node: BaseFlexiblePageTreeNode): node is DisclosureContentTreeNode => {
  return node.data.type === FlexiblePageTreeNodeType.DisclosureContent;
};

const typeLabelMap: Record<FlexiblePageTreeNodeType, string> = {
  [FlexiblePageTreeNodeType.Page]: 'Page',
  [FlexiblePageTreeNodeType.Section]: 'Section',
  [FlexiblePageTreeNodeType.Container]: 'Container',
  [FlexiblePageTreeNodeType.Text]: 'Text',
  [FlexiblePageTreeNodeType.Button]: 'Button',
  [FlexiblePageTreeNodeType.StructuredButton]: 'Button',
  [FlexiblePageTreeNodeType.Image]: 'Image',
  [FlexiblePageTreeNodeType.Icon]: 'Icon',
  [FlexiblePageTreeNodeType.Svg]: 'SVG',
  [FlexiblePageTreeNodeType.Video]: 'Video',
  [FlexiblePageTreeNodeType.Embed]: 'Embed',
  [FlexiblePageTreeNodeType.Dialog]: 'Modal',
  [FlexiblePageTreeNodeType.TabGroup]: 'Tab group',
  [FlexiblePageTreeNodeType.TabList]: 'Tab list',
  [FlexiblePageTreeNodeType.TabPanels]: 'Tab panels',
  [FlexiblePageTreeNodeType.Disclosure]: 'Accordion Panel',
  [FlexiblePageTreeNodeType.DisclosureContent]: 'Accordion content',
};

export const getNodeLabel = (nodeType: FlexiblePageTreeNodeType): string => {
  return typeLabelMap[nodeType] || 'Unknown';
};

// Type-to-schema mapping for runtime validation
// Maps each node type to its corresponding generation schema for type-aware update validation
const NodeTypeToGenerationSchemaMap: Record<FlexiblePageTreeNodeType, z.ZodType<unknown>> = {
  [FlexiblePageTreeNodeType.Button]: ButtonTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.StructuredButton]: StructuredButtonTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Text]: TextTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Container]: ContainerTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Image]: ImageTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Video]: VideoTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Icon]: IconTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Svg]: SvgTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Embed]: EmbedTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Section]: SectionTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Dialog]: DialogTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.TabGroup]: TabGroupTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.TabList]: TabListTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.TabPanels]: TabPanelsTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Disclosure]: DisclosureTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.DisclosureContent]: DisclosureContentTreeNodeDataGenerationSchema,
  [FlexiblePageTreeNodeType.Page]: PageTreeNodeDataSchema,
};

/**
 * Get the generation schema for a node type. Used for type-aware validation of update actions.
 * @param nodeType The FlexiblePageTreeNodeType to get the schema for
 * @returns The Zod schema for the node type, or null if unknown
 */
export function getGenerationSchemaForNodeType(nodeType: FlexiblePageTreeNodeType): z.ZodType<unknown> | null {
  return NodeTypeToGenerationSchemaMap[nodeType] ?? null;
}

// =============================================================================
// Streaming Types
// =============================================================================
// During JSON streaming, tree nodes are built incrementally:
// - Children arrays may contain undefined slots (placeholders)
// - Data properties may not have arrived yet
// - RichText fields may be partially populated
// - The node itself might be undefined
//
// These types make streaming handling explicit in components.

/**
 * Type for RichText props that may receive streaming (partial) data at runtime.
 */
type RichTextOrStreamingOrString = RichText | StreamingRichText | string;

/**
 * Utility type that replaces RichText | string fields with RichTextOrStreamingOrString.
 * This allows tree node data types to accept partially-streamed RichText at runtime.
 *
 * Only affects fields where RichText is part of the type union (e.g., RichText | string).
 * Also handles fields that include FlexibleBlockNode[] (the new FlexibleTextContent format).
 * Plain string fields (like className) are left unchanged.
 */
type WithStreamingRichText<T> = {
  [K in keyof T]: [RichText] extends [Exclude<T[K], undefined>]
    ?
        | RichTextOrStreamingOrString
        | Exclude<T[K], RichText | string | undefined>
        | (undefined extends T[K] ? undefined : never)
    : T[K];
};

// Streaming/prop-safe data types:
// - Derived from *Base schemas (pre-catchall) to avoid index signatures that
//   conflict with React component prop intersections in Zod 4
// - WithStreamingRichText allows RichText fields to accept partial/streaming data
// - Partial makes all properties optional (data may not have arrived yet)
//
// Note: The full *TreeNodeData types (from schemas with .catchall(JSONValueSchema))
// retain their index signatures for multiplayer store compatibility (Record<string, JSONValue>).
// These prop-safe types are used in component props where index signatures cause conflicts.
type PropSafeNodeData<Base extends z.ZodRawShape, NodeType extends FlexiblePageTreeNodeType> = z.infer<
  z.ZodObject<Base>
> & { type: NodeType };

// Prop-safe data types (no index signature) — use these in React component prop types.
// For runtime validation and multiplayer store compat, use the *TreeNodeData types above.
export type TextTreeNodeDataProps = PropSafeNodeData<
  (typeof TextTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Text
>;
export type ButtonTreeNodeDataProps = PropSafeNodeData<
  (typeof ButtonTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Button
>;
type StructuredButtonTreeNodeDataProps = PropSafeNodeData<
  (typeof StructuredButtonTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.StructuredButton
>;
export type ContainerTreeNodeDataProps = PropSafeNodeData<
  (typeof ContainerTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Container
>;
export type ImageTreeNodeDataProps = PropSafeNodeData<
  (typeof ImageTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Image
>;
export type IconTreeNodeDataProps = PropSafeNodeData<
  (typeof IconTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Icon
>;
export type VideoTreeNodeDataProps = PropSafeNodeData<
  (typeof VideoTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Video
>;
export type SvgTreeNodeDataProps = PropSafeNodeData<
  (typeof SvgTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Svg
>;
export type EmbedTreeNodeDataProps = PropSafeNodeData<
  (typeof EmbedTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Embed
>;
export type DialogTreeNodeDataProps = PropSafeNodeData<
  (typeof DialogTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Dialog
>;
export type SectionTreeNodeDataProps = PropSafeNodeData<
  (typeof SectionTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Section
>;
export type TabGroupTreeNodeDataProps = PropSafeNodeData<
  (typeof TabGroupTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.TabGroup
>;
export type TabListTreeNodeDataProps = PropSafeNodeData<
  (typeof TabListTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.TabList
>;
export type TabPanelsTreeNodeDataProps = PropSafeNodeData<
  (typeof TabPanelsTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.TabPanels
>;
export type DisclosureTreeNodeDataProps = PropSafeNodeData<
  (typeof DisclosureTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.Disclosure
>;
export type DisclosureContentTreeNodeDataProps = PropSafeNodeData<
  (typeof DisclosureContentTreeNodeDataBase)['shape'],
  FlexiblePageTreeNodeType.DisclosureContent
>;

export type StreamingTextTreeNodeData = Partial<WithStreamingRichText<TextTreeNodeDataProps>>;
export type StreamingButtonTreeNodeData = Partial<WithStreamingRichText<ButtonTreeNodeDataProps>>;
export type StreamingStructuredButtonTreeNodeData = Partial<WithStreamingRichText<StructuredButtonTreeNodeDataProps>>;
export type StreamingContainerTreeNodeData = Partial<WithStreamingRichText<ContainerTreeNodeDataProps>>;
export type StreamingImageTreeNodeData = Partial<WithStreamingRichText<ImageTreeNodeDataProps>>;
export type StreamingIconTreeNodeData = Partial<WithStreamingRichText<IconTreeNodeDataProps>>;
export type StreamingVideoTreeNodeData = Partial<VideoTreeNodeDataProps>;
export type StreamingSvgTreeNodeData = Partial<SvgTreeNodeDataProps>;
export type StreamingEmbedTreeNodeData = Partial<EmbedTreeNodeDataProps>;
export type StreamingDialogTreeNodeData = Partial<DialogTreeNodeDataProps>;
export type StreamingSectionTreeNodeData = Partial<SectionTreeNodeDataProps>;
export type StreamingTabGroupTreeNodeData = Partial<TabGroupTreeNodeDataProps>;
export type StreamingTabListTreeNodeData = Partial<TabListTreeNodeDataProps>;
export type StreamingTabPanelsTreeNodeData = Partial<TabPanelsTreeNodeDataProps>;
export type StreamingDisclosureTreeNodeData = Partial<DisclosureTreeNodeDataProps>;
export type StreamingDisclosureContentTreeNodeData = Partial<DisclosureContentTreeNodeDataProps>;

// Union of all streaming data types
export type StreamingComponentTreeNodeData =
  | StreamingTextTreeNodeData
  | StreamingButtonTreeNodeData
  | StreamingStructuredButtonTreeNodeData
  | StreamingContainerTreeNodeData
  | StreamingImageTreeNodeData
  | StreamingVideoTreeNodeData
  | StreamingSvgTreeNodeData
  | StreamingIconTreeNodeData
  | StreamingEmbedTreeNodeData
  | StreamingDialogTreeNodeData
  | StreamingSectionTreeNodeData
  | StreamingTabGroupTreeNodeData
  | StreamingTabListTreeNodeData
  | StreamingTabPanelsTreeNodeData
  | StreamingDisclosureTreeNodeData
  | StreamingDisclosureContentTreeNodeData;

/**
 * A tree node during streaming. The structure may be partially populated:
 * - `id` is always present (assigned when node is created)
 * - `data` properties (including `type`) may be undefined (not yet streamed)
 * - `children` may contain undefined slots
 */
export type StreamingComponentTreeNode = {
  id: string;
  data?: StreamingComponentTreeNodeData;
  children?: (StreamingComponentTreeNode | undefined)[];
};

/**
 * Type guard to check if a streaming node slot is defined (not undefined/null).
 */
export function isDefinedNode(node: StreamingComponentTreeNode | undefined | null): node is StreamingComponentTreeNode {
  return node != null;
}
